/**
 * data/site.ts — the business defaults (brand + contact).
 *
 * NOTHING here is the final source of truth at runtime: these are only
 * *fallback defaults*. The live values come from the Firestore
 * `settings/business` document (editable from the Phase-2 Settings page),
 * overlaid by `useBusiness()`. The mutable contact fields can also be
 * overridden at build time via VITE_BUSINESS_* env vars, so you never edit
 * code to change an address or email.
 *
 * Precedence (lowest → highest):
 *   literal defaults below  →  VITE_BUSINESS_* (.env)  →  settings/business (Firestore)
 */

const env = import.meta.env;

export interface BusinessHours {
  day: string;
  time: string;
}

export interface Business {
  name: string;
  legalName: string;
  wordmark: string;
  tagline: string;
  subTagline: string;
  city: string;
  country: string;
  address: string;
  phone: string;
  phoneDial: string;
  whatsapp: string;
  /** Address shown publicly on the site. */
  contactEmail: string;
  /** Inbox that receives booking notifications (server-side). */
  bookingsEmail: string;
  /** Operator/admin address. */
  adminEmail: string;
  hours: BusinessHours[];
  social: { instagram: string; facebook: string };
  payment: {
    payToCell: string;
    orangeMoney: string;
    myzaka: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
    branchCode: string;
  };
}

export const SITE: Business = {
  name: 'Grace Haven Spa',
  legalName: 'Grace Haven BNB & Spa',
  wordmark: 'Grace Haven',
  tagline: 'Comfort · Elegance · Relaxation',
  subTagline: 'Your haven of relaxation and comfort',
  city: env.VITE_BUSINESS_CITY || 'Gaborone',
  country: 'Botswana',
  address: env.VITE_BUSINESS_ADDRESS || 'Block 10, Plot 57966, Gaborone',
  phone: env.VITE_BUSINESS_PHONE || '+267 72 900 800',
  phoneDial: env.VITE_BUSINESS_PHONE_DIAL || '+26772900800',
  whatsapp: env.VITE_BUSINESS_WHATSAPP || '26772900800',
  contactEmail: env.VITE_BUSINESS_CONTACT_EMAIL || 'sapulangaka@gmail.com',
  bookingsEmail: env.VITE_BUSINESS_BOOKINGS_EMAIL || 'sapulangaka@gmail.com',
  adminEmail: env.VITE_BUSINESS_ADMIN_EMAIL || 'sapulangaka@gmail.com',
  hours: [
    { day: 'Monday – Friday', time: '09:00 – 19:00' },
    { day: 'Saturday', time: '08:00 – 18:00' },
    { day: 'Sunday', time: '10:00 – 16:00' },
  ],
  social: {
    instagram: env.VITE_BUSINESS_INSTAGRAM || 'https://instagram.com/gracehavenspa',
    facebook: env.VITE_BUSINESS_FACEBOOK || 'https://facebook.com/gracehavenspa',
  },
  payment: {
    payToCell: env.VITE_PAY_TO_CELL || '72 900 800',
    orangeMoney: env.VITE_ORANGE_MONEY || '72 900 800',
    myzaka: env.VITE_MYZAKA || '72 900 800',
    bankName: env.VITE_BANK_NAME || 'First National Bank (FNB)',
    accountName: env.VITE_BANK_ACCOUNT_NAME || 'Grace Haven BNB & Spa',
    accountNumber: env.VITE_BANK_ACCOUNT_NUMBER || '0000000000',
    branchCode: env.VITE_BANK_BRANCH || '282267',
  },
};

/** Build a WhatsApp deep link for any number + message. */
export function waLink(whatsapp: string, message: string): string {
  return `https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`;
}
